import { useStoreWithEqualityFn } from 'zustand/traditional'

import type {
  EqualityChecker,
  StateSelector,
  UseBoundStore,
} from '@core/zustand'

import { getGlobalStore } from './globalStore'
import type { IGlobalState } from './state'

export type IGlobalEqualityChecker = EqualityChecker<IGlobalState>

type UseGlobalStore = UseBoundStore<IGlobalState> &
  (<U>(
    selector: StateSelector<IGlobalState, U>,
    equalityFn?: EqualityChecker<U>,
  ) => U)

/**
 * Stable fallback for calls without a selector. It must keep a constant
 * identity: useSyncExternalStoreWithSelector memoizes on the selector
 * reference, so a fresh function each render would re-subscribe every render.
 */
const identitySelector = (state: IGlobalState) => state

/**
 * CS-1330: We cannot share hook across binaries. This can cause preact runtime
 * issues if the customer websites load two different Firework scripts.
 */
let useGlobalStore: UseGlobalStore | undefined

/**
 *
 */
export const resetUseGlobalStore = () => {
  useGlobalStore = undefined
}

/**
 *
 */
export const getGlobalStoreHook = () => {
  if (!useGlobalStore) {
    const store = getGlobalStore()
    // zustand 5 dropped create(existingStore) and bound-hook equality
    // support; zustand/traditional provides both.
    const hook = (<U>(
      selector?: StateSelector<IGlobalState, U>,
      equalityFn?: EqualityChecker<U>,
    ) =>
      useStoreWithEqualityFn(
        store,
        selector ?? (identitySelector as StateSelector<IGlobalState, U>),
        equalityFn,
      )) as UseGlobalStore
    useGlobalStore = Object.assign(hook, store)
  }
  return useGlobalStore as UseGlobalStore
}
